import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Clock, 
  CheckSquare, 
  FolderOpen, 
  Activity, 
  Filter,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toUTCPlus5DateTime } from '../utils/time';

const MyActivity = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalActivities, setTotalActivities] = useState(0);
  const [pageSize] = useState(20);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    action_type: '',
    table_name: '',
    date_from: '',
    date_to: '',
    search: '',
    user_id: '' // Add user filter for admin
  });

  // Team members for admin user filter
  const [teamMembers, setTeamMembers] = useState([]);

  // Filter activities based on search term and other filters
  const filteredActivities = activities.filter(activity => {
    // Apply search filter across multiple fields
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = (
        (activity.action_type && activity.action_type.toLowerCase().includes(searchLower)) ||
        (activity.table_name && activity.table_name.toLowerCase().includes(searchLower)) ||
        (activity.details && JSON.stringify(activity.details).toLowerCase().includes(searchLower)) ||
        (activity.user_name && activity.user_name.toLowerCase().includes(searchLower)) ||
        (activity.user_email && activity.user_email.toLowerCase().includes(searchLower))
      );
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  useEffect(() => {
    fetchActivities();
  }, [currentPage, filters]);

  // Fetch team members for admin user filter
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchTeamMembers();
    }
  }, [user]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get('/api/team');
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      
      // Build query parameters - filter out empty values
      const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
      );
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        ...filteredParams
      });

      // Use different endpoints based on user role
      let endpoint = '/api/activity-logs/my-activity';
      if (user?.role === 'admin') {
        endpoint = '/api/activity-logs/system';
      }

      const response = await axios.get(`${endpoint}?${params}`);
      const activities = response.data || [];
      setActivities(activities);
      
      // Get total count from the first activity if available, otherwise use response length
      let total = 0;
      if (activities.length > 0 && activities[0]._total_count !== undefined) {
        total = activities[0]._total_count;
      } else {
        // Fallback: if no total count, assume we have more data if we got a full page
        total = activities.length === pageSize ? currentPage * pageSize + 1 : activities.length;
      }
      
      setTotalActivities(total);
      setTotalPages(Math.ceil(total / pageSize));
      
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      toast.error('Failed to fetch activities');
      setActivities([]);
      setTotalActivities(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshLoading(true);
      await fetchActivities();
      toast.success('Activities refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh activities:', error);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      action_type: '',
      table_name: '',
      date_from: '',
      date_to: '',
      search: '',
      user_id: ''
    });
    setCurrentPage(1);
  };

  const formatActivityMessage = (activity) => {
    const { action_type, table_name, details, record_id } = activity;
    
    // Helper function to extract task name from various possible locations
    const getTaskName = (activity) => {
      // Try different possible locations for task name
      if (activity.details?.task_name) return activity.details.task_name;
      if (activity.details?.name) return activity.details.name;
      if (activity.details?.title) return activity.details.title;
      if (activity.details?.task_title) return activity.details.task_title;
      if (activity.details?.description) return activity.details.description;
      
      // If no task name in details, try to get from the main activity object
      if (activity.task_name) return activity.task_name;
      if (activity.name) return activity.name;
      if (activity.title) return activity.title;
      
      // For existing activities without task names, try to provide context
      if (activity.table_name === 'tasks' && activity.record_id) {
        // This is a task-related activity, show a generic but contextual message
        return 'Task';
      }
      
      // Don't show task ID, just show generic task name
      return 'Task';
    };
    
    // Helper function to extract project name
    const getProjectName = (activity) => {
      if (activity.details?.project_name) return activity.details.project_name;
      if (activity.details?.project_title) return activity.details.project_title;
      if (activity.details?.project) return activity.details.project;
      if (activity.project_name) return activity.project_name;
      if (activity.project_title) return activity.project_title;
      return 'Project';
    };
    
    const taskName = getTaskName(activity);
    const projectName = getProjectName(activity);
    
    switch (action_type) {
      case 'task_created':
        return `Task "${taskName}" created`;
      case 'task_updated':
        return `Task "${taskName}" updated`;
      case 'task_status_changed':
        const newStatus = activity.details?.new_status || activity.details?.status || 'Updated';
        return `Task "${taskName}" status changed to ${newStatus}`;
      case 'task_assigned':
        return `Task "${taskName}" assigned to you`;
      case 'task_unassigned':
        return `Task "${taskName}" unassigned`;
      case 'task_comment_added':
        return `Comment added to task "${taskName}"`;
      case 'task_comment_updated':
        return `Comment updated on task "${taskName}"`;
      case 'task_comment_deleted':
        return `Comment removed from task "${taskName}"`;
      case 'task_screenshot_uploaded':
        return `Screenshot uploaded for task "${taskName}"`;
      case 'task_screenshot_deleted':
        return `Screenshot removed from task "${taskName}"`;
      case 'time_entry_created':
        return `Time entry created for task "${taskName}"`;
      case 'time_entry_updated':
        return `Time entry updated for task "${taskName}"`;
      case 'time_entry_deleted':
        return `Time entry deleted for task "${taskName}"`;
      case 'timer_started':
        return `Timer started for task "${taskName}"`;
      case 'timer_stopped':
        return `Timer stopped for task "${taskName}"`;
      case 'project_created':
        return `Project "${projectName}" created`;
      case 'project_updated':
        return `Project "${projectName}" updated`;
      case 'project_deleted':
        return `Project "${projectName}" deleted`;
      case 'project_member_added':
        return `Member added to project "${projectName}"`;
      case 'project_member_removed':
        return `Member removed from project "${projectName}"`;
      case 'user_created':
        return `User account created`;
      case 'user_updated':
        return `User account updated`;
      case 'user_deleted':
        return `User account deleted`;
      case 'user_login':
        return `Logged in to the system`;
      case 'user_logout':
        return `Logged out of the system`;
      case 'user_password_changed':
        return `Password changed`;
      case 'user_email_verified':
        return `Email verified`;
      case 'user_password_reset':
        return `Password reset`;
      case 'invoice_created':
        return `Invoice created for ${projectName}`;
      case 'invoice_updated':
        return `Invoice updated for ${projectName}`;
      case 'invoice_deleted':
        return `Invoice deleted for ${projectName}`;
      case 'invoice_sent':
        return `Invoice sent for ${projectName}`;
      case 'invoice_paid':
        return `Invoice paid for ${projectName}`;
      case 'notification_created':
        return `Notification received`;
      case 'notification_read':
        return `Notification marked as read`;
      case 'notification_deleted':
        return `Notification deleted`;
      case 'note_created':
        if (activity.details?.note_type === 'task') {
          return `Note created on task "${taskName}"`;
        } else if (activity.details?.note_type === 'project') {
          return `Note created on project "${projectName}"`;
        }
        return `Note created`;
      case 'note_updated':
        if (activity.details?.note_type === 'task') {
          return `Note updated on task "${taskName}"`;
        } else if (activity.details?.note_type === 'project') {
          return `Note updated on project "${projectName}"`;
        }
        return `Note updated`;
      case 'note_deleted':
        if (activity.details?.note_type === 'task') {
          return `Note deleted from task "${taskName}"`;
        } else if (activity.details?.note_type === 'project') {
          return `Note deleted from project "${projectName}"`;
        }
        return `Note deleted`;
      case 'leave_created':
        return `Leave Created`;
      case 'leave_updated':
        return `Leave Updated`;
      case 'leave_deleted':
        return `Leave Deleted`;
      default:
        // Convert action_type to readable format and provide context
        const readableAction = action_type
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        
        // Provide context based on table_name
        switch (table_name) {
          case 'tasks':
            return `${readableAction} on task "${taskName}"`;
          case 'projects':
            return `${readableAction} on project "${projectName}"`;
          case 'time_entries':
            return `${readableAction} on time tracking`;
          case 'users':
            return `${readableAction} on user account`;
          case 'invoices':
            return `${readableAction} on invoice`;
          case 'notifications':
            return `${readableAction} on notification`;
      default:
            return `${readableAction} on ${table_name.replace(/_/g, ' ')}`;
        }
    }
  };

  const getActivityIcon = (action_type) => {
    switch (action_type) {
      case 'task_created':
      case 'task_updated':
      case 'task_status_changed':
      case 'task_assigned':
      case 'task_unassigned':
      case 'task_comment_added':
      case 'task_comment_updated':
      case 'task_comment_deleted':
      case 'task_screenshot_uploaded':
      case 'task_screenshot_deleted':
        return <CheckSquare className="h-5 w-5 text-green-600" />;
      case 'time_entry_created':
      case 'time_entry_updated':
      case 'time_entry_deleted':
      case 'timer_started':
      case 'timer_stopped':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'project_created':
      case 'project_updated':
      case 'project_deleted':
      case 'project_member_added':
      case 'project_member_removed':
        return <FolderOpen className="h-5 w-5 text-purple-600" />;
      case 'user_created':
      case 'user_updated':
      case 'user_deleted':
      case 'user_login':
      case 'user_logout':
      case 'user_password_changed':
      case 'user_email_verified':
      case 'user_password_reset':
        return <Activity className="h-5 w-5 text-orange-600" />;
      case 'invoice_created':
      case 'invoice_updated':
      case 'invoice_deleted':
      case 'invoice_sent':
      case 'invoice_paid':
        return <Activity className="h-5 w-5 text-indigo-600" />;
      case 'notification_created':
      case 'notification_read':
      case 'notification_deleted':
        return <Activity className="h-5 w-5 text-pink-600" />;
      case 'note_created':
      case 'note_updated':
      case 'note_deleted':
        return <Activity className="h-5 w-5 text-yellow-600" />;
      case 'leave_created':
      case 'leave_updated':
      case 'leave_deleted':
        return <Calendar className="h-5 w-5 text-teal-600" />;
      default:
        return <Activity className="h-5 w-5 text-secondary-600" />;
    }
  };





  const formatDateTime = (timestamp) => {
    return toUTCPlus5DateTime(timestamp);
  };

  const getActionTypeLabel = (actionType) => {
    return actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTableNameLabel = (tableName) => {
    return tableName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-12">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-secondary-900 mb-2">
              {user?.role === 'admin' ? 'System Activity' : 'My Activity'}
            </h1>
            <p className="text-secondary-600">
              {user?.role === 'admin' 
                ? 'Monitor all system activities and user actions across the platform'
                : 'Track all your activities and actions across the system'
              }
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            disabled={refreshLoading}
          >
            {refreshLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {refreshLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-4 lg:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-secondary-600" />
          <h2 className="text-lg font-semibold text-secondary-900">Filters</h2>
        </div>
        
        <div className={`grid gap-4 ${
          user?.role === 'admin' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5' 
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search activities..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* User Filter - Only for Admin */}
          {user?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                User
              </label>
              <select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Users</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.full_name || member.username}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Action Type - Only for Admin */}
          {user?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Action Type
              </label>
              <select
                value={filters.action_type}
                onChange={(e) => handleFilterChange('action_type', e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Actions</option>
                 <option value="user_created">User Created</option>
                 <option value="user_updated">User Updated</option>
                 <option value="user_deleted">User Deleted</option>
                 <option value="user_login">User Login</option>
                 <option value="user_logout">User Logout</option>
                 <option value="user_password_changed">Password Changed</option>
                 <option value="user_email_verified">Email Verified</option>
                 <option value="user_password_reset">Password Reset</option>
                 <option value="project_created">Project Created</option>
                 <option value="project_updated">Project Updated</option>
                 <option value="project_deleted">Project Deleted</option>
                 <option value="project_member_added">Member Added</option>
                 <option value="project_member_removed">Member Removed</option>
                 <option value="task_created">Task Created</option>
                 <option value="task_updated">Task Updated</option>
                 <option value="task_deleted">Task Deleted</option>
                 <option value="task_assigned">Task Assigned</option>
                 <option value="task_unassigned">Task Unassigned</option>
                 <option value="task_status_changed">Status Changed</option>
                 <option value="task_comment_added">Comment Added</option>
                 <option value="task_comment_deleted">Comment Deleted</option>
                 <option value="task_screenshot_uploaded">Screenshot Uploaded</option>
                 <option value="task_screenshot_deleted">Screenshot Deleted</option>
                 <option value="time_entry_created">Time Entry Created</option>
                 <option value="time_entry_updated">Time Entry Updated</option>
                 <option value="time_entry_deleted">Time Entry Deleted</option>
                 <option value="timer_started">Timer Started</option>
                 <option value="timer_stopped">Timer Stopped</option>
                 <option value="notification_created">Notification Created</option>
                 <option value="notification_read">Notification Read</option>
                 <option value="notification_deleted">Notification Deleted</option>
                 
              </select>
            </div>
          )}

          {/* Table Name */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Table
            </label>
            <select
              value={filters.table_name}
              onChange={(e) => handleFilterChange('table_name', e.target.value)}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Tables</option>
              <option value="tasks">Tasks</option>
              <option value="projects">Projects</option>
              <option value="time_entries">Time Entries</option>
              <option value="users">Users</option>
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Date From
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Date To and Clear Filters */}
        <div className="flex items-end gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Date To
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-secondary-600 border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Activities List */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-4 lg:p-6">
        {/* Search Results Info */}
        {filters.search && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-blue-800">
                <span className="font-medium">Search Results:</span> Found {filteredActivities.length} activit{filteredActivities.length !== 1 ? 'ies' : 'y'} matching "{filters.search}"
              </div>
              <button
                onClick={() => handleFilterChange('search', '')}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Clear Search
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-secondary-900">
            Activities ({filteredActivities.length} of {totalActivities})
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {filters.search ? 'No activities match your search' : 'No activities found'}
            </h3>
            <p className="text-secondary-600">
              {filters.search 
                ? `No activities match "${filters.search}"`
                : Object.values(filters).some(f => f) 
                  ? 'Try adjusting your filters' 
                  : user?.role === 'admin' 
                    ? 'No system activities found'
                    : 'You haven\'t performed any activities yet'
              }
            </p>
            {filters.search && (
              <button
                onClick={() => handleFilterChange('search', '')}
                className="btn-secondary mt-3"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {filteredActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getActivityIcon(activity.action_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-medium text-secondary-900 group-hover:text-primary-600 transition-colors">
                          {formatActivityMessage(activity)}
                        </h3>
                        <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                          {getActionTypeLabel(activity.action_type)}
                        </span>
                        <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full">
                          {getTableNameLabel(activity.table_name)}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDateTime(activity.timestamp)}</span>
                        </div>
                        {/* Show user info for admin users */}
                        {user?.role === 'admin' && activity.user_name && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-secondary-400">•</span>
                            <span className="text-secondary-600">
                              by <span className="font-medium">{activity.user_name}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    

                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                <div className="text-sm text-secondary-600">
                  Showing page {currentPage} of {totalPages}
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-3 py-2 text-sm border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyActivity;
